import {
  Button,
  Chip,
  Collapse,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  Input,
  InputLabel,
  makeStyles,
  MenuItem,
  MenuProps,
  Select,
  TextField,
  Theme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Alert from "@material-ui/lab/Alert";
import React, { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Officer } from "../../interfaces";
import {
  beachActions,
  beachSelectors,
  cityActions,
  citySelectors,
  officerActions,
  officerSelectors,
} from "../../redux";
import useOfficerForm, { OfficerFormType } from "./useOfficerForm";

interface FormDialogOfficerProps {
  officerToUpdate?: Officer;
  onClose: () => any;
  isOpen: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    chips: {
      display: "flex",
      flexWrap: "wrap",
    },
    chip: {
      margin: 2,
    },
    label: {
      marginTop: theme.spacing(2),
    },
  })
);

const FormDialogOfficer: React.FC<FormDialogOfficerProps> = ({
  officerToUpdate,
  onClose,
  isOpen,
}) => {
  const [alertOpen, setOpen] = React.useState(true);
  const classes = useStyles();

  const {
    errors,
    register,
    handleSubmit,
    control,
    reset,
    getValues,
    setValue,
  } = useOfficerForm();
  const [hasBeenRequested, setHasBeenRequested] = useState(false);
  const dispatch = useDispatch();
  const cities = useSelector(citySelectors.getAllCities);
  const createdOfficer = useSelector(officerSelectors.getCreatedOfficer);
  const updatedOfficer = useSelector(officerSelectors.getUpdatedOfficer);
  const beaches = useSelector(beachSelectors.getAllBeaches);
  const menuProps: Partial<MenuProps> | undefined = {
    PaperProps: {
      variant: "outlined",
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };
  useEffect(() => {
    if (!isOpen) {
      !officerToUpdate
        ? dispatch(officerActions.clear("createdOfficer"))
        : dispatch(officerActions.clear("updatedOfficer"));

      setHasBeenRequested(false);
    }
  }, [dispatch, isOpen, officerToUpdate]);

  useEffect(() => {
    dispatch(cityActions.loadAllCity({}));
    dispatch(beachActions.loadAllBeach({}));
  }, [dispatch]);

  useEffect(() => {
    if (
      officerToUpdate &&
      officerToUpdate.email &&
      officerToUpdate.displayName &&
      officerToUpdate.phoneNumber
    ) {
      reset({
        email: officerToUpdate.email,
        name: officerToUpdate.displayName,
        phoneNumber: officerToUpdate.phoneNumber.replace("+39", ""),
        id: officerToUpdate.uid,
        cities: officerToUpdate.cities,
        beaches: officerToUpdate.beaches,
      });
    }
  }, [officerToUpdate, reset]);

  useEffect(() => {
    if (createdOfficer.data || updatedOfficer.data) {
      reset({});
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  }, [createdOfficer, updatedOfficer, dispatch, onClose, reset]);

  const onSubmit = (data: OfficerFormType) => {
    if (officerToUpdate) {
      dispatch(
        officerActions.updateOfficer({
          uid: officerToUpdate.uid,
          displayName: data.name,
          phoneNumber: "+39" + data.phoneNumber,
          photoURL: "",
          email: data.email,
          beaches: data.beaches,
          cities: data.cities,
        })
      );
    } else
      dispatch(
        officerActions.createOfficer({
          displayName: data.name,
          phoneNumber: "+39" + data.phoneNumber,
          photoURL: "",
          email: data.email,
          beaches: data.beaches,
          cities: data.cities,
        })
      );
    setHasBeenRequested(true);
    dispatch(officerActions.loadAllOfficer({}));
  };

  const citiesOptions = cities.data?.results.map((city) => ({
    value: city.id,
    label: city.name,
  }));

  const beachesOptions = beaches.data?.results.map((beach) => ({
    value: beach.id,
    label: beach.name,
  }));

  return (
    <Dialog open={isOpen} onClose={onClose} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">
        Aggiungi un nuovo controllore
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <input name="id" ref={register} type="hidden"></input>
        <DialogContent>
          <DialogContentText>
            <Collapse
              in={
                (!createdOfficer.isLoading || !updatedOfficer.isLoading) &&
                hasBeenRequested &&
                alertOpen
              }
            >
              <Alert
                color={
                  createdOfficer.error || updatedOfficer.error
                    ? "error"
                    : "success"
                }
                severity={createdOfficer.error ? "error" : "success"}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setOpen(false);
                    }}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {createdOfficer.error || updatedOfficer.error
                  ? "Si è verificato un errore"
                  : "Dato aggiunto correttamente"}
              </Alert>
            </Collapse>
          </DialogContentText>

          <TextField
            label="Nome completo"
            helperText={errors.name?.message}
            error={!!errors.name}
            name="name"
            inputRef={register}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
          <TextField
            id="outlined-full-width"
            label="Email"
            helperText={errors.email?.message}
            fullWidth
            error={!!errors.email}
            name="email"
            inputRef={register}
            type="email"
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
          <TextField
            label="Numero di telefono"
            helperText={errors.phoneNumber?.message}
            inputRef={register}
            error={!!errors.phoneNumber}
            name="phoneNumber"
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
          />
          {citiesOptions && (
            <Controller
              as={
                <FormControl fullWidth className={classes.label}>
                  <InputLabel id="demo-mutiple-chip-label">Città</InputLabel>
                  <Select
                    onChange={(evt) =>
                      setValue("cities", evt.target.value as string[])
                    }
                    fullWidth
                    labelId="demo-mutiple-chip-label"
                    id="demo-mutiple-chip"
                    multiple
                    error={!!errors.cities?.length}
                    defaultValue={getValues("cities") || []}
                    input={<Input id="select-multiple-chip" />}
                    renderValue={(selected) => (
                      <div className={classes.chips}>
                        {(selected as string[]).map((value) => (
                          <Chip
                            key={value}
                            label={
                              citiesOptions.find((f) => f.value === value)
                                ?.label
                            }
                            className={classes.chip}
                          />
                        ))}
                      </div>
                    )}
                    MenuProps={menuProps}
                  >
                    {citiesOptions
                      ? citiesOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      : []}
                  </Select>
                  <FormHelperText error={!!errors.cities}>
                    {errors.cities ? (errors.cities as any).message : ""}
                  </FormHelperText>
                </FormControl>
              }
              name="cities"
              control={control}
              defaultValue={[]}
            />
          )}

          {beachesOptions && (
            <Controller
              as={
                <FormControl fullWidth className={classes.label}>
                  <InputLabel id="multiple-beaches-label">Spiagge</InputLabel>
                  <Select
                    onChange={(evt) =>
                      setValue("beaches", evt.target.value as string[])
                    }
                    fullWidth
                    labelId="multiple-beaches-label"
                    id="multiple-beaches-chip"
                    multiple
                    // helperText={errors.beaches?.map((r) => r.message).join(', ')}
                    defaultValue={getValues("beaches") || []}
                    input={<Input id="select-multiple-beaches" />}
                    renderValue={(selected) => (
                      <div className={classes.chips}>
                        {(selected as string[]).map((value) => (
                          <Chip
                            key={value}
                            label={
                              beachesOptions.find((f) => f.value === value)
                                ?.label
                            }
                            className={classes.chip}
                          />
                        ))}
                      </div>
                    )}
                    MenuProps={menuProps}
                  >
                    {beachesOptions
                      ? beachesOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))
                      : []}
                  </Select>
                  <FormHelperText error={!!errors.beaches}>
                    {errors.beaches ? (errors.beaches as any).message : ""}
                  </FormHelperText>
                </FormControl>
              }
              name="beaches"
              control={control}
              defaultValue={[]}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Chiudi
          </Button>
          <Button type="submit" color="primary">
            Aggiungi
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FormDialogOfficer;
